/**
 * Preferencia de codec e ajuste fino do SDP.
 *
 * O Chrome comeca uma transmissao nova em ~300 kbps e leva dezenas de segundos
 * para subir ate 12 Mbps. `x-google-start-bitrate` corta essa rampa; sem ele o
 * primeiro minuto de 1080p60 sai borrado. As funcoes sao puras sobre a string
 * de SDP e devolvem a entrada intacta quando a secao alvo nao existe.
 */

import { PREFERRED_VIDEO_CODECS } from "@shared/protocol";

const EOL = "\r\n";

/** Codecs auxiliares: nao recebem parametros de bitrate. */
const HELPER_CODECS = new Set(["rtx", "red", "ulpfec", "flexfec", "flexfec-03", "cn", "telephone-event"]);

export type VideoTuning = {
  startBitrateKbps?: number | null;
  maxBitrateKbps?: number | null;
  minBitrateKbps?: number | null;
};

export type OpusTuning = {
  /** Teto medio do Opus em bits por segundo. */
  bitrate?: number | null;
  stereo?: boolean;
};

export type SdpTuning = VideoTuning & {
  opusBitrate?: number | null;
  stereo?: boolean;
  /** Regra por secao de video: `[0]` camera, `[1]` tela. */
  videoBySection?: VideoTuning[];
  /** Regra por secao de audio: `[0]` microfone, `[1]` audio da tela. */
  opusBySection?: OpusTuning[];
};

type Section = { kind: string; start: number; end: number };

/**
 * Reordena os codecs de video do transceiver segundo `PREFERRED_VIDEO_CODECS`.
 * A lista completa e mantida (rtx, red e fec continuam presentes), so a ordem muda.
 */
export function preferCodecs(transceiver: RTCRtpTransceiver): void {
  if (typeof transceiver.setCodecPreferences !== "function") return;
  if (typeof RTCRtpReceiver === "undefined") return;
  if (typeof RTCRtpReceiver.getCapabilities !== "function") return;
  const capabilities = RTCRtpReceiver.getCapabilities("video");
  if (!capabilities || capabilities.codecs.length === 0) return;
  const ranked = [...capabilities.codecs].sort((a, b) => rankOf(a.mimeType) - rankOf(b.mimeType));
  try {
    transceiver.setCodecPreferences(ranked);
  } catch {
    // Alguns navegadores recusam a lista inteira; a negociacao segue no padrao.
  }
}

function rankOf(mimeType: string): number {
  const index = PREFERRED_VIDEO_CODECS.findIndex(
    name => name.toLowerCase() === mimeType.toLowerCase(),
  );
  return index === -1 ? PREFERRED_VIDEO_CODECS.length : index;
}

export function tuneSdp(sdp: string, tuning: SdpTuning): string {
  if (!sdp) return sdp;
  const lines = sdp.split(/\r\n|\r|\n/);
  const sections = parseSections(lines);
  if (sections.length === 0) return sdp;

  const out: string[] = lines.slice(0, sections[0].start);
  let videoIndex = 0;
  let audioIndex = 0;

  for (const section of sections) {
    const body = lines.slice(section.start, section.end);
    if (section.kind === "video") {
      out.push(...tuneVideoSection(body, videoRule(tuning, videoIndex)));
      videoIndex += 1;
    } else if (section.kind === "audio") {
      out.push(...tuneAudioSection(body, audioRule(tuning, audioIndex)));
      audioIndex += 1;
    } else {
      out.push(...body);
    }
  }
  return out.join(EOL);
}

function videoRule(tuning: SdpTuning, index: number): VideoTuning {
  const specific = tuning.videoBySection?.[index];
  if (specific) return specific;
  return {
    startBitrateKbps: tuning.startBitrateKbps,
    maxBitrateKbps: tuning.maxBitrateKbps,
    minBitrateKbps: tuning.minBitrateKbps,
  };
}

function audioRule(tuning: SdpTuning, index: number): OpusTuning {
  const specific = tuning.opusBySection?.[index];
  if (specific) return specific;
  return { bitrate: tuning.opusBitrate, stereo: tuning.stereo };
}

function parseSections(lines: string[]): Section[] {
  const sections: Section[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    if (!lines[index].startsWith("m=")) continue;
    const kind = lines[index].slice(2).split(" ")[0] ?? "";
    if (sections.length > 0) sections[sections.length - 1].end = index;
    sections.push({ kind, start: index, end: lines.length });
  }
  return sections;
}

function tuneVideoSection(body: string[], rule: VideoTuning): string[] {
  const params = new Map<string, string>();
  if (typeof rule.startBitrateKbps === "number") {
    params.set("x-google-start-bitrate", String(Math.round(rule.startBitrateKbps)));
  }
  if (typeof rule.maxBitrateKbps === "number") {
    params.set("x-google-max-bitrate", String(Math.round(rule.maxBitrateKbps)));
  }
  if (typeof rule.minBitrateKbps === "number") {
    params.set("x-google-min-bitrate", String(Math.round(rule.minBitrateKbps)));
  }
  if (params.size === 0) return body;
  return applyFmtp(body, params);
}

function tuneAudioSection(body: string[], rule: OpusTuning): string[] {
  const params = new Map<string, string>();
  if (rule.stereo !== undefined) {
    params.set("stereo", rule.stereo ? "1" : "0");
    params.set("sprop-stereo", rule.stereo ? "1" : "0");
  }
  if (typeof rule.bitrate === "number") {
    params.set("maxaveragebitrate", String(Math.round(rule.bitrate)));
  }
  if (params.size === 0) return body;
  params.set("useinbandfec", "1");
  params.set("usedtx", "0");
  return applyFmtp(body, params, payload => payload === "opus");
}

/**
 * Injeta `params` no `a=fmtp` de cada codec da secao, criando a linha quando
 * ela nao existe. `accept` filtra pelo nome do codec (usado pelo Opus).
 */
function applyFmtp(
  body: string[],
  params: Map<string, string>,
  accept?: (codecName: string) => boolean,
): string[] {
  const codecs = new Map<string, string>();
  const withFmtp = new Set<string>();

  for (const line of body) {
    const rtpmap = /^a=rtpmap:(\d+)\s+([^/\s]+)\//.exec(line);
    if (rtpmap) codecs.set(rtpmap[1], rtpmap[2].toLowerCase());
    const fmtp = /^a=fmtp:(\d+)\s/.exec(line);
    if (fmtp) withFmtp.add(fmtp[1]);
  }

  const targets = new Set<string>();
  for (const [payload, name] of codecs) {
    if (HELPER_CODECS.has(name)) continue;
    if (accept && !accept(name)) continue;
    targets.add(payload);
  }
  if (targets.size === 0) return body;

  const out: string[] = [];
  for (const line of body) {
    const fmtp = /^a=fmtp:(\d+)\s+(.*)$/.exec(line);
    if (fmtp && targets.has(fmtp[1])) {
      out.push(`a=fmtp:${fmtp[1]} ${mergeFmtp(fmtp[2], params)}`);
      continue;
    }
    out.push(line);
    const rtpmap = /^a=rtpmap:(\d+)\s/.exec(line);
    if (rtpmap && targets.has(rtpmap[1]) && !withFmtp.has(rtpmap[1])) {
      out.push(`a=fmtp:${rtpmap[1]} ${mergeFmtp("", params)}`);
    }
  }
  return out;
}

function mergeFmtp(existing: string, params: Map<string, string>): string {
  const entries = new Map<string, string | null>();
  for (const chunk of existing.split(";")) {
    const item = chunk.trim();
    if (!item) continue;
    const eq = item.indexOf("=");
    if (eq === -1) entries.set(item, null);
    else entries.set(item.slice(0, eq), item.slice(eq + 1));
  }
  for (const [key, value] of params) entries.set(key, value);
  return [...entries]
    .map(([key, value]) => (value === null ? key : `${key}=${value}`))
    .join(";");
}
